// @ts-nocheck

import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Route, Switch } from 'react-router-dom';
import styled, { createGlobalStyle, css } from 'styled-components';

import Client, { isIOSEmbeddedAndStandalone } from '../../client';
import { getUserAccountFormUrl } from '../../config/urls';
import { ModalContentSpacing, ModalFormat } from '../../constants/ui/modal';
import { SportsMenuScrollContext } from '../../contexts/SportsMenuScrollContext';
import { getAppDOMElement, getPortalRootDOMElement } from '../../DOMElements';
import { usePrevious } from '../../hooks';
import useRouter from '../../hooks/useRouter';
import { __, getLang } from '../../i18n/l10n';
import { isIPhoneX, isIPhoneXSMax } from '../../lib/useragent';
import OddsTravelAnimationTriggererContainer from '../../modules/animations/containers/OddsTravelAnimationTriggererContainer';
import BetslipOptionsRouterSwitch from '../../modules/betslip-options/components/BetslipOptionsRouterSwitch/BetslipOptionsRouterSwitch';
import ClientUpdaterContainer from '../../modules/client-updater/components/ClientUpdater/ClientUpdaterContainer';
import GRRouterSwitch from '../../modules/gift-race/components/GRRouterSwitch';
import GlobalStyleGrid from '../../modules/grids/components/GlobalStyleGrid';
import GridsModalsMobile from '../../modules/grids/components/modals/GridsModalsMobile';
import { isGridMobileModalOpen, isGrids } from '../../modules/grids/utils/routes';
import { isGridHelpOpen } from '../../modules/grids/utils/routes';
import KLRouterSwitch from '../../modules/koh-lanta/components/KLRouterSwitch';
import MissionsRouterSwitch from '../../modules/missions/components/MissionsRouterSwitch';
import { Modal as ModalTheme } from '../../styled-components/dark/modal';
import { LayoutMobileStyle } from '../../styled-components/mobile';
import { Licences } from '../../types/common';
import { RoutePatterns } from '../../types/routes';
import AuthBettingPlugin from '../../utils/plugins/authBetting';
import InAppBrowser from '../../utils/plugins/InAppBrowser';
import { getUserhasConnected } from '../../utils/userStorage';
import BasketMobileModalContainer from '../BasketMobileModal/BasketMobileModalContainer';
import FloatingBasketButtonContainer from '../FloatingBasketButton/FloatingBasketButtonContainer';
import HistoryContainer from '../History/HistoryContainer';
import HistoryItemDetailContainer from '../HistoryItemDetail/HistoryItemDetailContainer';
import LandingContainer from '../Landing/LandingContainer';
import LegalWrapperContainer from '../LegalWrapper/LegalWrapperContainer';
import MobileLoginModalView from '../MobileLoginModalView';
import MobileSportsRouteViewContainer from '../MobileSportsRouteView/MobileSportsRouteViewContainer';
import Modal from '../Modal';
import { Overlay } from '../Modal/Modal.style';
import NavigationBarContainer from '../NavigationBar/NavigationBarContainer';
import NavigationBurgerMenuContainer from '../NavigationBurgerMenu/NavigationBurgerMenuContainer';
import NavigationFreebetsCoinsTravelAnimationTriggerer from '../NavigationFreebetsCoinsTravelAnimationTriggerer';
import NavigationRealCoinsTravelAnimationTriggerer from '../NavigationRealCoinsTravelAnimationTriggerer';
import NavigationTabBarContainer from '../NavigationTabBar/NavigationTabBarContainer';
import QRScannerView from '../QRScannerView';
import RegisterModal from '../RegisterModal';
import ReviewContainer from '../Review/ReviewContainer';
import RoutesCalendarContainer from '../RoutesCalendar/RoutesCalendarContainer';
import RoutesCatalogContainer from '../RoutesCatalog/RoutesCatalogContainer';
import RoutesFavoriteContainer from '../RoutesFavorite/RoutesFavoriteContainer';
import RoutesHomeContainer from '../RoutesHome/RoutesHomeContainer';
import RoutesLiveContainer from '../RoutesLive/RoutesLiveContainer';
import RoutesMatchContainer from '../RoutesMatch/RoutesMatchContainer';
import ScaleRouteTransitionGroup from '../ScaleRouteTransitionGroup';
import ScaleTransitionGroup from '../ScaleTransitionGroup';
import SearchBarContainer from '../SearchBar/SearchBarContainer';
import SpinnerWithText from '../SpinnerWithText';
import SportRadarPlayerWidgetContainer from '../SportRadarPlayerWidget/SportRadarPlayerWidgetContainer';
import SportRadarWidget from '../SportRadarWidget';
import UserAccountGraphicsPreferencesModalView from '../UserAccountGraphicsPreferencesModalView';
import UserAccountModalView from '../UserAccountModalView';
import WinaLoader from '../WinaLoader';

//https://stackoverflow.com/questions/47134329/wkwebview-page-height-issue-on-iphone-x
const GlobalStyleIphoneXOrXSMax = createGlobalStyle`
${({ $isIphoneXSMax }) =>
  $isIphoneXSMax
    ? css`
        @media only screen and (orientation: portrait) {
          html.app-mobile {
            height: 896px;
            width: 414px;
          }
        }
        @media only screen and (orientation: landscape) {
          html.app-mobile {
            width: 896px;
            height: 414px;
          }
        }
      `
    : css`
        @media only screen and (orientation: portrait) {
          html.app-mobile {
            height: 812px;
            width: 375px;
          }
        }
        @media only screen and (orientation: landscape) {
          html.app-mobile {
            width: 812px;
            height: 375px;
          }
        }
      `}

`;

const GlobalStyleToRemoveTouchCallOut = createGlobalStyle`
  a {
    -webkit-touch-callout: none;
  }
`;

const GridsRouteContainer = lazy(() => import('../../modules/grids/containers/routes/GridsRouteContainer'));
const GridsHistoryDetailRouteContainer = lazy(
  () => import('../../modules/grids/containers/routes/GridsHistoryDetailRouteContainer'),
);
const GridsShareRouteContainer = lazy(() => import('../../modules/grids/containers/routes/GridsShareRouteContainer'));
const GridsPublicRouteContainer = lazy(() => import('../../modules/grids/containers/routes/GridsPublicRouteContainer'));
const GridsHistoryRouteContainer = lazy(
  () => import('../../modules/grids/containers/routes/GridsHistoryRouteContainer'),
);

const MonopolyRouterSwitch = lazy(() => import('../../modules/monopoly/components/MPRouterSwitch'));
const BoomRouterSwitch = lazy(() => import('../../modules/boom/components/BoomRouterSwitch'));

const StyledSpinnerWithText = styled(SpinnerWithText)`
  position: absolute;
  top: 0;
  left: 0;
`;

const LegalRoute = ({ children, ...rest }: RouteType) => {
  return (
    <LegalWrapperContainer>
      <Route {...rest}>{children}</Route>
    </LegalWrapperContainer>
  );
};

type MobileLayoutProps = {
  connected: boolean;
  doLoginEmbedded: (props: { licence: string; onLoginDismissed: () => void }) => void;
  openExternalLink: (url: string, options?: unknown) => void;
  licence: string;
  jwt: string | null;
};

const MobileLayout = (props: MobileLayoutProps) => {
  const { connected, doLoginEmbedded, openExternalLink, licence, jwt } = props;

  const { history, location } = useRouter();

  const previousLocation = usePrevious(location);

  const isBasketOpen = useMemo(() => location.hash.indexOf('#cart') === 0, [location.hash]);
  const isSrPlayerWidgetOpen = location.state?.playerWidgetProps;
  const isBrWidgetOpen = location.state?.brWidgetProps;
  const isSearchOpen = useMemo(() => location.hash.indexOf('#search') === 0, [location.hash]);
  const isScannerOpen = useMemo(() => location.hash.indexOf('#scanner') === 0, [location.hash]);
  const isHelpOpen = isGridHelpOpen(location);
  const isGridsRoute = isGrids(location.pathname);
  const showFloatingBasket = !isGridsRoute;
  const prevHash = usePrevious(location.hash);
  const hasConnected = useMemo(() => getUserhasConnected(), []);

  const [isOpen, setIsOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(
    !hasConnected && !connected && [Licences.DE, Licences.FR].includes(licence),
  );
  const [isUserAccountOpen, setIsUserAccountOpen] = useState(false);
  const [userAccountSubPath, setUserAccountSubPath] = useState(null);

  // State des scroll Positions dans le sports menu
  const [scrollPositions, setScrollPositions] = useState({});

  // Lorsqu'on est authentifié via le poker, si on voit qu'un des 2 flags de connexion change, on enlève la modale
  useEffect(() => {
    if (hasConnected || connected) {
      setShowRegisterModal(false);
    }
  }, [hasConnected, connected]);

  useEffect(() => {
    async function checkLogin() {
      if (location.hash.indexOf('#login') === 0) {
        if (!prevHash || prevHash.indexOf('#login') !== 0) {
          // On vient de passer sur l'écran login
          const available = await AuthBettingPlugin.isAvailable();

          if (Client.embed && available) {
            doLoginEmbedded({
              licence,
              onLoginDismissed: () => history.goBackOrReplace(location.pathname + location.search),
            });
          } else {
            setIsLoginOpen(true);
          }
        }
      } else {
        setIsLoginOpen(false);
      }
    }

    checkLogin();
  }, [doLoginEmbedded, history, licence, location.search, location.pathname, location.hash, prevHash]);

  useEffect(() => {
    async function openUserAccount() {
      if (location.hash.indexOf('#user-account') === 0) {
        if (!prevHash || prevHash.indexOf('#user-account') !== 0) {
          const userAccountSubPath = location.hash?.split('/')?.[1];

          if (Client.embed && InAppBrowser.isAvailable()) {
            // On enlève tout de suite le hash pour se prévenir d'une redirection à la sortie de l'inappbrowser
            history.goBackOrReplace(location.pathname + location.search);

            await InAppBrowser.open(getUserAccountFormUrl(), {
              jwt,
              lang: getLang(),
              licence,
              application: Client.application,
              platform: Client.platform,
              store: Client.store,
              store_id: Client.storeId,
              app_version: Client.appVersion,
              betting_version: Client.bettingVersion,
              oid: Client.tracker,
              path: userAccountSubPath,
            });
          } else {
            setIsUserAccountOpen(true);
            setUserAccountSubPath(userAccountSubPath);
          }
        }
      } else {
        setUserAccountSubPath(null);
        setIsUserAccountOpen(false);
      }
    }
    openUserAccount();
  }, [history, jwt, licence, location, prevHash]);

  const toggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const closeModal = useCallback(() => {
    if (previousLocation?.hash) {
      history.goBackOrReplace(location.pathname);
    } else {
      history.replace(location.pathname);
    }
  }, [history, location.pathname, previousLocation?.hash]);

  const providedState = useMemo(
    () => ({
      scrollPositions,
      setScrollPositions,
    }),
    [scrollPositions, setScrollPositions],
  );

  return (
    <SportsMenuScrollContext.Provider value={providedState}>
      <LayoutMobileStyle className={`app-layout-mobile ${isOpen ? 'opened' : ''}`}>
        {isIOSEmbeddedAndStandalone && (isIPhoneX || isIPhoneXSMax) ? (
          <GlobalStyleIphoneXOrXSMax isIPhoneXSMax={isIPhoneXSMax} />
        ) : null}
        {isIOSEmbeddedAndStandalone ? <GlobalStyleToRemoveTouchCallOut /> : null}
        <>
          {/* Fixed */}

          {!showRegisterModal && !isLoginOpen
            ? createPortal(<NavigationBarContainer toggleMenu={toggleMenu} />, getAppDOMElement())
            : null}
          {createPortal(<NavigationTabBarContainer />, getAppDOMElement())}

          {/* Body */}
          <LandingContainer />
          <Suspense fallback={<WinaLoader />}>
            <Switch>
              <Route path={RoutePatterns.GRIDS}>
                <GlobalStyleGrid>
                  <Switch>
                    <Route path={RoutePatterns.GRID_SHARE} exact>
                      <GridsShareRouteContainer />
                    </Route>
                    <Route path={RoutePatterns.GRID_HISTORY_DETAIL} exact>
                      <GridsHistoryDetailRouteContainer />
                    </Route>
                    <Route path={RoutePatterns.GRID_HISTORY} exact>
                      <GridsHistoryRouteContainer />
                    </Route>
                    <Route path={RoutePatterns.GRID_PUBLIC} exact>
                      <GridsPublicRouteContainer />
                    </Route>
                    <Route path={RoutePatterns.POOL} exact>
                      <GridsRouteContainer />
                    </Route>
                  </Switch>
                </GlobalStyleGrid>
              </Route>
            </Switch>
            <KLRouterSwitch />
            <MissionsRouterSwitch />
            <GRRouterSwitch />
            <MonopolyRouterSwitch />
            <BoomRouterSwitch />
            <BetslipOptionsRouterSwitch />
            <Switch>
              <LegalRoute path={RoutePatterns.HOME} exact>
                <RoutesHomeContainer />
              </LegalRoute>
              <LegalRoute path={RoutePatterns.FAVORITES} exact>
                <RoutesFavoriteContainer />
              </LegalRoute>
              <LegalRoute path={RoutePatterns.LIVE} exact>
                <RoutesLiveContainer />
              </LegalRoute>
              <LegalRoute path={RoutePatterns.CATALOG} exact>
                <RoutesCatalogContainer />
              </LegalRoute>
              <LegalRoute path={RoutePatterns.SPORTS} exact>
                <MobileSportsRouteViewContainer />
              </LegalRoute>
              <LegalRoute path={RoutePatterns.CALENDAR} exact>
                <RoutesCalendarContainer />
              </LegalRoute>
              <LegalRoute path={RoutePatterns.HISTORY} exact>
                <HistoryContainer />
              </LegalRoute>
              <LegalRoute path={RoutePatterns.HISTORY_ITEM_DETAIL} exact>
                <HistoryItemDetailContainer />
              </LegalRoute>
              <LegalRoute path={RoutePatterns.MATCH}>
                <RoutesMatchContainer />
              </LegalRoute>
              <LegalRoute path={RoutePatterns.MATCH_AND_BASKETITEM} exact>
                <RoutesMatchContainer />
              </LegalRoute>
              <LegalRoute path={RoutePatterns.BETBUILDER} exact>
                <RoutesMatchContainer />
              </LegalRoute>
            </Switch>
          </Suspense>
          {/* Modal */}
          {createPortal(
            <>
              {/* Overlay */}
              <Switch>
                <Route path={RoutePatterns.GRIDS}>
                  {isBasketOpen || isGridMobileModalOpen(location) ? (
                    <Overlay $zIndex={isGridsRoute ? 150 : 140} />
                  ) : null}
                </Route>
              </Switch>
              {/* Basket */}
              <Switch>
                <Route path={RoutePatterns.GRIDS} />
                <Route>
                  <BasketMobileModalContainer isOpen={isBasketOpen} closeModal={closeModal} />
                </Route>
              </Switch>

              <ScaleTransitionGroup>
                {/* Search */}
                {isSearchOpen ? (
                  <Modal contentSpacing={ModalContentSpacing.NONE} format={ModalFormat.FULLSCREEN} hasNavBarSpacing>
                    <SearchBarContainer />
                  </Modal>
                ) : null}
                {/* Login */}
                {isLoginOpen ? <MobileLoginModalView /> : null}
                {/* User Account */}
                {isUserAccountOpen ? <UserAccountModalView subpath={userAccountSubPath} /> : null}
                {/* Sportradar player widget */}
                {isSrPlayerWidgetOpen ? (
                  <Modal
                    backgroundColor={ModalTheme.backgroundColor.widgetPlayer}
                    // Some css for betradar is already loaded for trackers. Need to override it
                    className="overrideTrackerCss"
                    contentSpacing={ModalContentSpacing.NONE}
                    format={ModalFormat.FULLSCREEN}
                    hasNavBarSpacing
                    useViewportSize
                  >
                    <SportRadarPlayerWidgetContainer
                      matchId={location.state?.playerWidgetProps.matchId}
                      playerSrId={location.state?.playerWidgetProps.playerSrId}
                      playerTeam={location.state?.playerWidgetProps.playerTeam}
                    />
                  </Modal>
                ) : null}
                {isBrWidgetOpen ? (
                  <Modal
                    backgroundColor={ModalTheme.backgroundColor.widgetPlayer}
                    // Some css for betradar is already loaded for trackers. Need to override it
                    className="overrideTrackerCss"
                    contentSpacing={ModalContentSpacing.NONE}
                    format={ModalFormat.FULLSCREEN}
                    hasNavBarSpacing
                    useViewportSize
                  >
                    <SportRadarWidget
                      displayWidget={isBrWidgetOpen}
                      commandKey={location.state?.brWidgetProps.commandKey}
                      options={location.state?.brWidgetProps.widgetOptions}
                    />
                  </Modal>
                ) : null}
              </ScaleTransitionGroup>
              {/* Grids modals */}
              <GridsModalsMobile isHelpOpen={isHelpOpen} isBasketOpen={isBasketOpen} />
              {/* Betbuilder */}
              <ScaleRouteTransitionGroup>
                <Route path={RoutePatterns.GRAPHICS_PREFERENCES} exact>
                  <UserAccountGraphicsPreferencesModalView />
                </Route>
                {/* QR Code Scanner Modal */}
                {isScannerOpen ? (
                  <Modal
                    backgroundColor={ModalTheme.backgroundColor.scanner}
                    contentSpacing={ModalContentSpacing.NONE}
                    format={ModalFormat.FULLSCREEN}
                    hasNavBarSpacing
                    disableScrollView
                  >
                    <Suspense fallback={<StyledSpinnerWithText text={__('Loading').toUpperCase()} />}>
                      <QRScannerView />
                    </Suspense>
                  </Modal>
                ) : null}
                {/* Register Modal */}
                {showRegisterModal ? (
                  <Modal
                    backgroundColor={ModalTheme.backgroundColor.default}
                    contentSpacing={ModalContentSpacing.NONE}
                    format={ModalFormat.FULLSCREEN}
                  >
                    <RegisterModal onClose={() => setShowRegisterModal(false)} openExternalLink={openExternalLink} />
                  </Modal>
                ) : null}
              </ScaleRouteTransitionGroup>
            </>,
            getPortalRootDOMElement(),
          )}
          {/* Menu */}
          {createPortal(
            <NavigationBurgerMenuContainer isOpen={isOpen} toggleMenu={toggleMenu} />,
            getPortalRootDOMElement(),
          )}
          {/* Review Dialog */}
          {createPortal(<ReviewContainer />, getPortalRootDOMElement())}
          {/* Client updater only for iOS atm */}
          {isIOSEmbeddedAndStandalone ? createPortal(<ClientUpdaterContainer />, getPortalRootDOMElement()) : null}
          {connected ? createPortal(<NavigationRealCoinsTravelAnimationTriggerer />, getAppDOMElement()) : null}
          {connected ? createPortal(<NavigationFreebetsCoinsTravelAnimationTriggerer />, getAppDOMElement()) : null}
          {showFloatingBasket ? createPortal(<FloatingBasketButtonContainer />, getPortalRootDOMElement()) : null}
          {createPortal(<OddsTravelAnimationTriggererContainer />, getPortalRootDOMElement())}
        </>
      </LayoutMobileStyle>
    </SportsMenuScrollContext.Provider>
  );
};

export default MobileLayout;
